import { useSyncExternalStore } from 'react';

import { CHAT_DAY_LIST_DRESS, type ListDto } from '@chat-emp/shared';
import type { ChannelId } from '../fixtures/domain.js';
import {
  addToList,
  deleteList,
  listLists,
  removeFromList,
  reorderLists,
  saveList,
} from '../lib/facade.js';

/**
 * A named, hand-picked set of conversations, stored per user by the BFF.
 *
 * The store lives on the module, so the picker in a dialog, the rail panel and
 * the inbox's own list scope — three mounts of this hook at once — all agree.
 * The user's lists are fetched once on the first mount; every mutation paints
 * the change locally first, then syncs it to the BFF, rolling back on failure.
 */

/**
 * The hues a list can wear. Stored as a key, not a colour, so the shade lives in
 * tokens.css. A key the set no longer names drops back to the default.
 */
export const LIST_COLORS = ['violet', 'teal', 'fuchsia', 'slate'] as const;
export type ListColor = (typeof LIST_COLORS)[number];

/** The glyphs a list can wear — twenty, none of the ones the app speaks with. */
export const LIST_ICONS = [
  'flame',
  'rocket',
  'target',
  'trophy',
  'crown',
  'gem',
  'anchor',
  'compass',
  'leaf',
  'sprout',
  'sun',
  'moon',
  'cloud',
  'snowflake',
  'coffee',
  'music',
  'camera',
  'palette',
  'puzzle',
  'mountain',
] as const;

/**
 * The mark a Chat Day's own list wears, and the only thing that says a list came
 * from a booking rather than from a person (AI-2744).
 *
 * The stored list is a name, an icon, a colour, a position and its members —
 * there is no field for where a list came from, and adding one is a change to
 * an upstream this app does not own. So the mark IS the dress: a reserved key
 * the editor's palette never offers (`ICON_OPTIONS` builds from `LIST_ICONS`,
 * which this is deliberately not in), drawn as the calendar the Event section
 * already speaks with. It earns the field twice over — it is what makes a
 * booked list recognisable down the column, and what the Chat Day chip filters
 * on.
 *
 * Renaming and recolouring keep the mark; picking a glyph of one's own drops
 * it, and the list becomes an ordinary one. That is the price of storing the
 * mark in the dress, and it is the cheaper price: deriving it instead by
 * matching list names against the diary would have quietly stopped working on
 * every older Chat Day, because the diary is fetched as a window around today
 * (`listEventsFor`) and an event that falls out of it would take its list's
 * mark with it.
 */
export const CHAT_DAY_ICON = CHAT_DAY_LIST_DRESS.icon;

/** A glyph an employer picked, or the Chat Day mark. */
export type ListIcon = (typeof LIST_ICONS)[number] | typeof CHAT_DAY_ICON;

/** Every key a stored icon may hold — the palette, plus the reserved mark. */
const ICON_KEYS: readonly string[] = [...LIST_ICONS, CHAT_DAY_ICON];

export interface ConversationList {
  /** Numeric, assigned by the BFF. Optimistic creates hold a negative placeholder. */
  id: number;
  name: string;
  /** How the list is dressed. Absent is the default bookmark on the accent. */
  icon?: ListIcon;
  color?: ListColor;
  /** Members, in the order they were added. */
  channelIds: ChannelId[];
}

/** How a list is dressed, with no name attached — what a badge needs to draw one. */
export type ListDress = Pick<ConversationList, 'icon' | 'color'>;

/** Everything about a list but its members: what the editor sets in one go. */
export type ListStyle = ListDress & { name: string };

/** Whether a booking filed this list, rather than a person. See `CHAT_DAY_ICON`. */
export function isChatDayList(list: ListDress): boolean {
  return list.icon === CHAT_DAY_ICON;
}

let snapshot: ConversationList[] = [];
// True until the one-time hydrate resolves, so a consumer can show a loading
// placeholder instead of reading a not-yet-fetched store as an empty one.
let loading = true;
const listeners = new Set<() => void>();
let hydrated = false;
// Placeholder ids for optimistic creates. Negative, so one can never collide
// with a server id (which is always positive) before the swap lands.
let tempSeq = -1;

function read(): ConversationList[] {
  return snapshot;
}

function readLoading(): boolean {
  return loading;
}

function notify(): void {
  for (const listener of listeners) listener();
}

function publish(next: ConversationList[]): void {
  snapshot = next;
  notify();
}

const ordered = (dtos: ListDto[]): ConversationList[] =>
  [...dtos].sort((a, b) => a.position - b.position).map(fromDto);

/** A stored list, made whole: an icon/colour the set no longer names drops out. */
function fromDto(dto: ListDto): ConversationList {
  return {
    id: dto.id,
    name: dto.name,
    icon: dto.icon && ICON_KEYS.includes(dto.icon) ? (dto.icon as ListIcon) : undefined,
    color:
      dto.color && (LIST_COLORS as readonly string[]).includes(dto.color)
        ? (dto.color as ListColor)
        : undefined,
    channelIds: dto.channelIds,
  };
}

/**
 * The user's lists, once, on the first mount, ordered by the server's position.
 *
 * ponytail: a local change made before this returns keeps the field to itself —
 * the fetched set is dropped rather than clobbering the optimistic one. A reload
 * loads server truth again; upgrade to a merge only if that window bites.
 */
async function hydrate(): Promise<void> {
  try {
    const dtos = await listLists();
    if (snapshot.length === 0) {
      snapshot = ordered(dtos);
    }
  } catch {
    hydrated = false; // a later mount retries
  } finally {
    // Resolved either way: stop showing the placeholder. A failed first load
    // then reads as empty until a later mount retries, which is the prior
    // behaviour — the win is only that the loading window no longer does.
    loading = false;
    notify();
  }
}

/** Re-read the lists after the BFF changed them (a Chat Day's list), keeping them if the read fails. */
export async function refreshLists(): Promise<void> {
  try {
    const dtos = await listLists();
    // Creates still in flight keep their placeholder rows, or their id swap would find nothing.
    publish([...snapshot.filter((list) => list.id < 0), ...ordered(dtos)]);
  } catch {
    // The next refresh or reload reads them again.
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (!hydrated) {
    hydrated = true;
    void hydrate();
  }
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Paint the change, fire the BFF call, put the pre-change set back if it fails.
 *
 * ponytail: last-writer rollback — a second mutation racing the first would
 * restore a stale set on failure. The lists UI is one user in one panel, so the
 * serial case is the real one; add per-list locks only if that stops holding.
 */
function sync(next: ConversationList[], call: () => Promise<unknown>): void {
  const before = snapshot;
  publish(next);
  call().catch(() => publish(before));
}

/**
 * Newest first, and returned synchronously with a placeholder id so the caller
 * (which files chats into it and opens it) never waits on the round trip. The
 * BFF's real id swaps in when the create lands; a failure drops the row.
 */
function create(style: ListStyle, channelIds: ChannelId[] = []): ConversationList {
  const placeholderId = tempSeq--;
  const optimistic: ConversationList = {
    ...style,
    id: placeholderId,
    channelIds: [...new Set(channelIds)],
  };
  publish([optimistic, ...snapshot]);
  saveList({
    name: style.name,
    icon: style.icon ?? null,
    color: style.color ?? null,
    channelIds: optimistic.channelIds,
  })
    // Only the id swaps in — the optimistic row already holds the name, dress
    // and members; saveList returns just the id. It is written onto the SAME
    // object create() returned, not a fresh copy: a caller that kept the row (the
    // "Added to …" toast reads `.id` at click time, seconds after the swap) would
    // open a dead placeholder id otherwise. The store map stays keyed on the
    // placeholder so the reconcile still lands if a sibling mutation replaced the
    // row in the store meanwhile — the returned reference is updated regardless.
    .then(({ id }) => {
      optimistic.id = id;
      publish(snapshot.map((list) => (list.id === placeholderId ? { ...list, id } : list)));
    })
    .catch(() => publish(snapshot.filter((list) => list.id !== placeholderId)));
  return optimistic;
}

/**
 * Change what a list looks like. Takes the whole style, not a patch: a spread
 * cannot tell "key omitted" from "key present and undefined", and clearing a
 * colour depends on the undefined arriving.
 */
function update(id: number, style: ListStyle): void {
  // Assign the fields, don't spread the style: a spread leaves an omitted
  // icon/colour untouched, but the body below clears it (`?? null`), so the
  // painted list has to drop it too or the two disagree until the next reload.
  sync(
    snapshot.map((list) =>
      list.id === id ? { ...list, name: style.name, icon: style.icon, color: style.color } : list,
    ),
    () => saveList({ id, name: style.name, icon: style.icon ?? null, color: style.color ?? null }),
  );
}

/** Move one list to where another one sits. */
function reorder(fromId: number, toId: number): void {
  if (fromId === toId) return;
  const from = snapshot.findIndex((list) => list.id === fromId);
  const to = snapshot.findIndex((list) => list.id === toId);
  if (from === -1 || to === -1) return;
  const next = [...snapshot];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved!);
  sync(next, () => reorderLists({ ids: next.map((list) => list.id) }));
}

function remove(id: number): void {
  sync(
    snapshot.filter((list) => list.id !== id),
    () => deleteList(id),
  );
}

/** Union, not append: adding chats already in the list changes nothing. */
function addTo(id: number, channelIds: ChannelId[]): void {
  sync(
    snapshot.map((list) =>
      list.id === id
        ? { ...list, channelIds: [...new Set([...list.channelIds, ...channelIds])] }
        : list,
    ),
    () => addToList({ id, channelIds }),
  );
}

function removeFrom(id: number, channelIds: ChannelId[]): void {
  const dropped = new Set(channelIds);
  sync(
    snapshot.map((list) =>
      list.id === id
        ? { ...list, channelIds: list.channelIds.filter((channelId) => !dropped.has(channelId)) }
        : list,
    ),
    () => removeFromList({ id, channelIds }),
  );
}

export interface UseLists {
  lists: ConversationList[];
  /** True until the one-time hydrate resolves; the rail shows a skeleton meanwhile. */
  loading: boolean;
  create: (style: ListStyle, channelIds?: ChannelId[]) => ConversationList;
  update: (id: number, style: ListStyle) => void;
  /** Move `fromId` to the position `toId` currently holds. */
  reorder: (fromId: number, toId: number) => void;
  remove: (id: number) => void;
  /** Add `channelIds` to the list, ignoring the ones already in it. */
  addTo: (id: number, channelIds: ChannelId[]) => void;
  removeFrom: (id: number, channelIds: ChannelId[]) => void;
}

export function useLists(): UseLists {
  const lists = useSyncExternalStore(subscribe, read);
  const loading = useSyncExternalStore(subscribe, readLoading);
  // The mutators are module-level and so already stable; only `lists`/`loading` change.
  return { lists, loading, create, update, reorder, remove, addTo, removeFrom };
}

/**
 * Test seam: drop the store so each case starts from nothing. `tempSeq` keeps
 * counting on purpose: a create still settling from the previous case would
 * otherwise reconcile onto the next case's row.
 */
export function resetListsForTest(): void {
  snapshot = [];
  loading = true;
  hydrated = false;
  listeners.clear();
}
